import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import * as api from '../services/api';
import { Category } from '../types';

interface FooterProps {
  hideProcurements?: boolean;
  hideUsefulTexts?: boolean;
}

const Footer: React.FC<FooterProps> = ({ hideProcurements, hideUsefulTexts }) => {
  const { t } = useScript();
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    api.getCategories()
      .then(data => {
        setCategories(data.filter(c => c.parentId === null));
      })
      .catch(console.error);
  }, []);

  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-columns">
          {/* Left column — Brand */}
          <div className="footer-col footer-col-brand">
            <div className="footer-brand">
              <div className="footer-logo">
                <img
                  src="/resource/logo/3.png"
                  alt="Манастир Градац"
                  className="footer-logo-img"
                />
              </div>
              <div>
                <div className="footer-title">{t("Манастир Градац")}</div>
                <div className="footer-subtitle">{t("Задужбина Свете краљице Јелене Анжујске")}</div>
              </div>
            </div>
            <p className="footer-desc">
              {t("Православни манастир из XIII века, смештен у долини реке Ибар на обронцима Голије.")}
            </p>
          </div>

          {/* Center column — Navigation */}
          <div className="footer-col footer-col-nav">
            <h4 className="footer-col-heading">{t("Навигација")}</h4>
            <nav className="footer-nav-vertical">
              <Link to="/" className="footer-nav-link">{t("Почетна")}</Link>
              
              {/* Dynamic categories from header */}
              {categories
                .filter(cat => !(hideUsefulTexts && cat.slug === 'korisni-tekstovi'))
                .map(cat => (
                <Link key={cat.id} to={`/${cat.slug}`} className="footer-nav-link">
                  {t(cat.title)}
                </Link>
              ))}

              <Link to="/galerija" className="footer-nav-link">{t("Галерија")}</Link>
              <Link to="/novosti" className="footer-nav-link">{t("Новости")}</Link>
              {!hideProcurements && (
                <Link to="/isporuke" className="footer-nav-link">{t("Јавне набавке")}</Link>
              )}
            </nav>
          </div>

          {/* Right column — Contact & Social */}
          <div className="footer-col footer-col-contact">
            <h4 className="footer-col-heading">{t("Контакт")}</h4>
            <div className="footer-contact-info">
              <div className="footer-contact-row">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>
                <span>{t("Манастир Градац, 36346 Брвеник, Рашка, Србија")}</span>
              </div>
              <div className="footer-contact-row">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                <span>manastir.gradac@gmail.com</span>
              </div>
              <div className="footer-contact-row">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                <span>+381 64 8003884</span>
              </div>
            </div>
            <div className="footer-social">
              <a
                href="https://www.instagram.com/manastirgradac/"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-link"
                aria-label="Instagram"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="5" />
                  <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
                </svg>
                <span>@manastirgradac</span>
              </a>
            </div>
          </div>
        </div>

        {/* Legal links section */}
        <div className="footer-legal">
          <Link to="/politika-privatnosti" className="footer-legal-link">{t("Политика приватности")}</Link>
          <span className="footer-legal-sep" aria-hidden="true">·</span>
          <Link to="/kolacici" className="footer-legal-link">{t("Колачићи")}</Link>
          <span className="footer-legal-sep" aria-hidden="true">·</span>
          <Link to="/uslovi-koriscenja" className="footer-legal-link">{t("Услови коришћења")}</Link>
          <span className="footer-legal-sep" aria-hidden="true">·</span>
          <Link to="/zasluge" className="footer-legal-link">{t("Заслуге за фотографије")}</Link>
        </div>

        {/* Bottom bar */}
        <div className="footer-bottom">
          <div>{t("© 2026 Манастир Градац. Сва права задржана.")}</div>
          <div className="footer-credit">{t("Фотографије: Светлана Дингарац")}</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
